import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { useEffect, useRef, useState } from "react";

import {
	getStripeJsLoadBlockedCode,
	loadStripeWithRetry,
	reduceStripeJsLoadState,
	type StripeJsLoadStatus,
} from "~/lib/stripe/stripeJsLoad";

type BlockedCode = ReturnType<typeof getStripeJsLoadBlockedCode>;

/**
 * Loads Stripe.js with retries and tracks ready/error so checkout can show a
 * retry UI instead of sitting on "Loading card form…" forever.
 *
 * @param options.enabled - When false, loading is paused
 * @param options.onBlocked - Fired once per failed load attempt cycle
 */
const useStripeJsLoad = (options?: {
	enabled?: boolean;
	onBlocked?: (code: BlockedCode) => void;
}) => {
	const enabled = options?.enabled ?? true;
	const onBlockedRef = useRef(options?.onBlocked);
	onBlockedRef.current = options?.onBlocked;

	const [status, setStatus] = useState<StripeJsLoadStatus>("loading");
	const [attemptKey, setAttemptKey] = useState(0);
	const [stripePromise, setStripePromise] =
		useState<Promise<Stripe | null> | null>(null);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		// Read attemptKey so retry() can remount a fresh load cycle.
		void attemptKey;
		setStatus("loading");

		const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!;
		const promise = loadStripeWithRetry(publishableKey, {
			loadStripe,
		}).then(
			(stripe) => {
				setStatus((current) => reduceStripeJsLoadState(current, "ready"));
				return stripe;
			},
			() => {
				setStatus((current) => {
					const next = reduceStripeJsLoadState(current, "load_error");
					if (next === "error" && current === "loading") {
						onBlockedRef.current?.(getStripeJsLoadBlockedCode());
					}
					return next;
				});
				return null;
			},
		);

		setStripePromise(promise);
	}, [attemptKey, enabled]);

	const retry = () => {
		setStatus((current) => reduceStripeJsLoadState(current, "retry"));
		setAttemptKey((key) => key + 1);
	};

	return {
		stripePromise,
		status,
		isReady: status === "ready",
		isError: status === "error",
		isLoading: status === "loading",
		retry,
	};
};

export default useStripeJsLoad;
